import type { ReactElement } from 'react';
import type { BridgeSuit } from '@strategy4kids/game-engine';
import { packCardUrl } from '../lib/card-packs.js';
import { useActivePack } from '../state/card-pack-store.js';

/**
 * Drawn card faces, pure SVG (no assets, nothing to load). One 100x140 card, painted in `currentColor`
 * for the suit ink so the four-colour deck and dark mode need no changes here.
 *
 * Two layouts, both with the usual corner index (rank, with a small suit under it; rotated at the
 * opposite corner):
 *   - `pips`  : the traditional face. The 10 of hearts carries ten hearts; A is one big suit; J/Q/K are
 *               drawn portraits.
 *   - `bold`  : one large suit in the middle (a big heart for the 10 of hearts) with the index in the
 *               corners. Easier to read at small sizes, so kids can pick it.
 */
export type CardFaceStyle = 'pips' | 'bold';

const W = 100;
const H = 140;

/** Suit shapes, each centred on 0,0 and about 20 units across. */
const SUIT_PATH: Record<BridgeSuit, string> = {
  H: 'M0 9.5 C-15 -1 -11 -10.5 -4.8 -9.6 C-2.2 -9.2 0 -6.8 0 -5 C0 -6.8 2.2 -9.2 4.8 -9.6 C11 -10.5 15 -1 0 9.5Z',
  D: 'M0 -11 C2.5 -5 5 -2.5 8 0 C5 2.5 2.5 5 0 11 C-2.5 5 -5 2.5 -8 0 C-5 -2.5 -2.5 -5 0 -11Z',
  S: 'M0 -11 C-15 0 -11 8 -5 7 C-3 6.7 -1.8 5.6 -1.7 5 C-1.7 8.4 -2.8 10.4 -5.5 11.5 L5.5 11.5 C2.8 10.4 1.7 8.4 1.7 5 C1.8 5.6 3 6.7 5 7 C11 8 15 0 0 -11Z',
  C: '',
};

/** Clubs sit a little high in their box; nudge so all four look level when set in a row. */
const SUIT_DY: Record<BridgeSuit, number> = { H: 0, D: 0, S: 0, C: 0 };

function Suit({ suit, x, y, scale = 1, flip = false }: { suit: BridgeSuit; x: number; y: number; scale?: number; flip?: boolean }) {
  const transform = `translate(${x} ${y + SUIT_DY[suit] * scale}) scale(${scale}) ${flip ? 'rotate(180)' : ''}`;
  if (suit === 'C') {
    // Clubs are three round leaves round a fat centre on a flared stem; circles read cleaner than one path.
    return (
      <g transform={transform} fill="currentColor" data-suit-shape={suit}>
        <circle cx="0" cy="-5.6" r="5.3" />
        <circle cx="-6.3" cy="2.4" r="5.3" />
        <circle cx="6.3" cy="2.4" r="5.3" />
        <circle cx="0" cy="0" r="3.8" />
        <path d="M0 0.5 C0.6 6 -1.6 9.6 -4.4 11.6 L4.4 11.6 C1.6 9.6 -0.6 6 0 0.5Z" />
      </g>
    );
  }
  return <path d={SUIT_PATH[suit]} fill="currentColor" transform={transform} data-suit-shape={suit} />;
}

const COL = { L: 30, C: 50, R: 70 } as const;
const ROW = { top: 32, a: 57.3, b: 82.7, bottom: 108, mid: 70 } as const;

type Pip = readonly [x: number, y: number];

/** Pip positions per rank (2-10), the traditional layouts. The lower half is drawn upside down. */
const PIPS: Record<number, readonly Pip[]> = {
  2: [[COL.C, ROW.top], [COL.C, ROW.bottom]],
  3: [[COL.C, ROW.top], [COL.C, ROW.mid], [COL.C, ROW.bottom]],
  4: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  5: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.C, ROW.mid], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  6: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.L, ROW.mid], [COL.R, ROW.mid], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  7: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.C, 50], [COL.L, ROW.mid], [COL.R, ROW.mid], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  8: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.C, 50], [COL.L, ROW.mid], [COL.R, ROW.mid], [COL.C, 90], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  9: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.L, ROW.a], [COL.R, ROW.a], [COL.C, ROW.mid], [COL.L, ROW.b], [COL.R, ROW.b], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
  10: [[COL.L, ROW.top], [COL.R, ROW.top], [COL.C, 44.5], [COL.L, ROW.a], [COL.R, ROW.a], [COL.L, ROW.b], [COL.R, ROW.b], [COL.C, 95.5], [COL.L, ROW.bottom], [COL.R, ROW.bottom]],
};

const RANK_TEXT: Record<number, string> = { 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 15: '★' };
const rankLabel = (rank: number): string => RANK_TEXT[rank] ?? String(rank);

const GOLD = '#f2b705';
const GOLD_DEEP = '#c88a04';
const SKIN = '#f7d6b5';
const HAIR_DARK = '#4a2f1a';
const HAIR_LIGHT = '#e6b04a';
const INK = '#1e2433';

/** The corner index: rank over a small suit. Drawn once, then again rotated about the card centre. */
function Index({ rank, suit }: { rank: number; suit: BridgeSuit }) {
  const label = rankLabel(rank);
  return (
    <g>
      <text
        x="12"
        y="20"
        textAnchor="middle"
        fontSize={label.length > 1 ? 15 : 19}
        fontWeight={800}
        fontFamily="inherit"
        fill="currentColor"
        letterSpacing={label.length > 1 ? -1.5 : 0}
      >
        {label}
      </text>
      {/* A Joker belongs to no suit: its star stands alone in the corner. */}
      {rank === 15 ? null : <Suit suit={suit} x={12} y={31} scale={0.62} />}
    </g>
  );
}

/** One half of a court card (drawn in the upper half; the lower half is the same, turned over). */
function Bust({ rank, suit }: { rank: 11 | 12 | 13; suit: BridgeSuit }) {
  const king = rank === 13;
  const queen = rank === 12;
  return (
    <g>
      {/* hair behind the head */}
      {queen && <path d="M38 38 C34 52 36 60 41 62 L59 62 C64 60 66 52 62 38 C58 28 42 28 38 38Z" fill={HAIR_LIGHT} />}
      {!queen && <path d="M41 38 C41 30 59 30 59 38 L59 44 L41 44Z" fill={king ? '#d8d8dc' : HAIR_DARK} />}
      {/* shoulders and robe, in the suit ink */}
      <path d="M24 70 L24 67 C26 59 37 55.5 50 55.5 C63 55.5 74 59 76 67 L76 70Z" fill="currentColor" />
      <path d="M24 66 L76 66 L76 69 L24 69Z" fill={GOLD} opacity="0.9" />
      <path d="M44 55.5 L50 66 L56 55.5Z" fill={GOLD} />
      {/* neck and head */}
      <rect x="46" y="48" width="8" height="8" rx="2" fill={SKIN} />
      <ellipse cx="50" cy="42" rx="9" ry="10" fill={SKIN} />
      {king && <path d="M41.5 44 C42 54 58 54 58.5 44 C55 47 45 47 41.5 44Z" fill="#e4e4e8" />}
      {/* face */}
      <circle cx="46.2" cy="41" r="1.1" fill={INK} />
      <circle cx="53.8" cy="41" r="1.1" fill={INK} />
      <path d={king ? 'M47 46.3 Q50 47.6 53 46.3' : 'M46.5 46 Q50 49 53.5 46'} stroke={king ? INK : '#c4553b'} strokeWidth="1.1" fill="none" strokeLinecap="round" />
      <circle cx="43.6" cy="44" r="1.8" fill="#f2a08a" opacity="0.55" />
      <circle cx="56.4" cy="44" r="1.8" fill="#f2a08a" opacity="0.55" />
      {/* headwear */}
      {king && (
        <g>
          <path d="M39 33 L38 21 L44.5 27 L50 18 L55.5 27 L62 21 L61 33Z" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="1" strokeLinejoin="round" />
          <rect x="39" y="31" width="22" height="4" rx="1" fill={GOLD_DEEP} />
          <circle cx="50" cy="16.5" r="2" fill="currentColor" />
          <circle cx="50" cy="26.5" r="1.7" fill="currentColor" />
        </g>
      )}
      {queen && (
        <g>
          <path d="M39 33 C40 22 60 22 61 33 C57 29.5 43 29.5 39 33Z" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="1" strokeLinejoin="round" />
          <path d="M45 26.5 L47 21 L50 25 L53 21 L55 26.5Z" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" strokeLinejoin="round" />
          <circle cx="50" cy="27" r="2" fill="currentColor" />
        </g>
      )}
      {!king && !queen && (
        <g>
          <path d="M38.5 35 C38 25 62 25 61.5 35 C57 31 43 31 38.5 35Z" fill="currentColor" />
          <path d="M59 30 C65 25 69 24 71 17 C65 18 61 22 57 27Z" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" strokeLinejoin="round" />
          <path d="M38.5 34 L61.5 34" stroke={GOLD_DEEP} strokeWidth="2.2" strokeLinecap="round" />
        </g>
      )}
      {/* collar jewels and the suit the court belongs to */}
      <circle cx="50" cy="64" r="2" fill={GOLD} />
      {queen && <path d="M40 57 Q50 65 60 57" stroke={GOLD} strokeWidth="1.6" fill="none" strokeLinecap="round" />}
      <Suit suit={suit} x={32} y={36} scale={0.62} />
    </g>
  );
}

const COURT_NAME: Record<11 | 12 | 13, string> = { 11: 'jack', 12: 'queen', 13: 'king' };
const SUIT_NAME: Record<BridgeSuit, string> = { S: 'spades', H: 'hearts', D: 'diamonds', C: 'clubs' };

/**
 * The generated portrait laid over the drawn bust, which stays as the fallback: the classic deck's
 * (public/cards/<court>-<suit>.webp) or, when the player picked a card pack that has a picture for this card, that pack's.
 */
function Portrait({ rank, suit }: { rank: 11 | 12 | 13; suit: BridgeSuit }) {
  const pack = useActivePack();
  return (
    <image
      href={(pack ? packCardUrl(pack, rank, suit) : null) ?? `/cards/${COURT_NAME[rank]}-${SUIT_NAME[suit]}.webp`}
      x="20"
      y="20"
      width="60"
      height="100"
      preserveAspectRatio="xMidYMid slice"
      clipPath="url(#court-clip)"
    />
  );
}

/** The picture frame a court (or a pack's Ace) sits in, and the clip its art is cut to. */
function CourtFrame() {
  return (
    <>
      <defs>
        <clipPath id="court-clip">
          <rect x="20" y="20" width="60" height="100" rx="3" />
        </clipPath>
      </defs>
      <rect x="21" y="22" width="58" height="96" rx="5" fill="currentColor" opacity="0.07" />
      <rect x="21" y="22" width="58" height="96" rx="5" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.55" />
      <rect x="24.5" y="25.5" width="51" height="89" rx="3" fill="none" stroke={GOLD} strokeWidth="0.9" opacity="0.9" />
    </>
  );
}

/** The Ace: one big suit in the middle, or the active pack's Ace picture in the court frame when it has one (the suit stays under it as the fallback). */
function AceFace({ suit }: { suit: BridgeSuit }) {
  const pack = useActivePack();
  const url = pack ? packCardUrl(pack, 14, suit) : null;
  if (!pack || !url) return <Suit suit={suit} x={50} y={70} scale={3.9} />; // no pack, or a pack with no Ace for this suit: the plain big suit
  return (
    <g>
      <CourtFrame />
      <Suit suit={suit} x={50} y={70} scale={3.9} />
      <image
        href={url}
        x="20"
        y="20"
        width="60"
        height="100"
        preserveAspectRatio="xMidYMid slice"
        clipPath="url(#court-clip)"
        data-pack-ace={pack.id}
      />
    </g>
  );
}

/** The classic deck's Joker: a jester in a three-pointed hat with bells, in the card's ink (red or black, from its suit). */
function JesterBust() {
  return (
    <g>
      <path d="M31 62 C28 48 23 40 25 31 C32 35 38 42 41 53 C43 40 46 32 50 24 C54 32 57 40 59 53 C62 42 68 35 75 31 C77 40 72 48 69 62Z" fill="currentColor" />
      <circle cx="25" cy="30" r="3.3" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" />
      <circle cx="50" cy="23" r="3.3" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" />
      <circle cx="75" cy="30" r="3.3" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" />
      <rect x="31" y="59" width="38" height="5" rx="1.5" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" />
      <ellipse cx="50" cy="76" rx="14" ry="15" fill={SKIN} />
      <circle cx="44.5" cy="73" r="1.4" fill={INK} />
      <circle cx="55.5" cy="73" r="1.4" fill={INK} />
      <path d="M43 80 Q50 88 57 80" stroke="#c4553b" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <circle cx="40.5" cy="79" r="2" fill="#f2a08a" opacity="0.55" />
      <circle cx="59.5" cy="79" r="2" fill="#f2a08a" opacity="0.55" />
      <path d="M29 101 L35 92 L41 101 L47 92 L53 101 L59 92 L65 101 L71 93 L71 108 L29 108Z" fill={GOLD} stroke={GOLD_DEEP} strokeWidth="0.8" strokeLinejoin="round" />
      <rect x="27" y="106" width="46" height="12" fill="currentColor" />
    </g>
  );
}

/** A Joker: the drawn jester, or the active pack's red or black Joker where it has one (the jester stays under it as the fallback). */
function JokerFace({ suit }: { suit: BridgeSuit }) {
  const pack = useActivePack();
  const url = pack ? packCardUrl(pack, 15, suit) : null;
  return (
    <g>
      <CourtFrame />
      <g clipPath="url(#court-clip)">
        <JesterBust />
      </g>
      {url ? (
        <image href={url} x="20" y="20" width="60" height="100" preserveAspectRatio="xMidYMid slice" clipPath="url(#court-clip)" data-pack-joker={pack?.id} />
      ) : null}
    </g>
  );
}

function Court({ rank, suit }: { rank: 11 | 12 | 13; suit: BridgeSuit }) {
  return (
    <g>
      <CourtFrame />
      <g clipPath="url(#court-clip)">
        <Bust rank={rank} suit={suit} />
        <g transform="rotate(180 50 70)">
          <Bust rank={rank} suit={suit} />
        </g>
      </g>
      <Portrait rank={rank} suit={suit} />
    </g>
  );
}

function Pips({ rank, suit }: { rank: number; suit: BridgeSuit }) {
  const layout = PIPS[rank] ?? [];
  return (
    <g>
      {layout.map(([x, y]) => (
        <Suit key={`${x}-${y}`} suit={suit} x={x} y={y} scale={rank <= 3 ? 1.35 : rank <= 6 ? 1.1 : 0.8} flip={y > ROW.mid} />
      ))}
    </g>
  );
}

export interface CardFaceProps {
  readonly rank: number;
  readonly suit: BridgeSuit;
  readonly style?: CardFaceStyle;
  readonly className?: string;
}

/** The face, as a decorative SVG: the card button/div around it carries the spoken name. */
export function CardFace({ rank, suit, style = 'pips', className }: CardFaceProps) {
  const court = rank >= 11 && rank <= 13;
  const ace = rank === 14;
  let body: ReactElement;
  if (rank === 15) {
    body = <JokerFace suit={suit} />;
  } else if (court) {
    body = <Court rank={rank as 11 | 12 | 13} suit={suit} />;
  } else if (style === 'bold' || ace) {
    // Bold: one big suit in the middle. The ace always looks like this (or carries the active pack's Ace art).
    body = ace ? <AceFace suit={suit} /> : <Suit suit={suit} x={50} y={70} scale={3.3} />;
  } else {
    body = <Pips rank={rank} suit={suit} />;
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} aria-hidden="true" focusable="false" data-face={style}>
      {body}
      <Index rank={rank} suit={suit} />
      <g transform={`rotate(180 ${W / 2} ${H / 2})`}>
        <Index rank={rank} suit={suit} />
      </g>
    </svg>
  );
}
